import { environment } from "../environments/environment";
import { BasicObjI as ObjectI } from "../Core/@types";
import { DIProps } from "../Core/DependencyInjection";
import { setEncryptedItem, getDecryptedItem, removeEncryptedItem } from "./secureSession";
import * as Sentry from '@sentry/react';

export interface GSI {
  set: (
    _StateProps: any
  ) => (key: string, value: string, path?: boolean) => void;
  get: (
    _StateProps: any
  ) => (key: string, prefixIncluded?: boolean) => string | null;
  removeLocalStorage: (
    _StateProps: DIProps
  ) => (key: string, path?: string) => unknown;
  getBearerToken: (_StateProps: any) => (prefixIncluded?: boolean) => string | null;
  prepareQuery: (params: ObjectI) => unknown;
}

const SelectedStorage = {
  setItem: setEncryptedItem,
  getItem: getDecryptedItem,
  removeItem: removeEncryptedItem,
};

export function getCurrentUserId(redux: any): string | undefined {
  return redux?.user_id || getDecryptedItem("user_id") || undefined;
}

export const globalState: GSI = {
  set:
    (_StateProps: any) =>
      (key: string, value: string, prefixIncluded = false): void => {

        if (value == null || value === undefined) {
        console.warn(`[globalState.set] Value for key "${key}" is null or undefined, skipping storage`);
        Sentry?.captureMessage?.(`[globalState.set] Value for key "${key}" is null or undefined`, {
          extra: { key, value, _StateProps },
        });
        return;
      }
      
        let originalKey = key; // For logging
        let effectiveKey = key; // The key that will be used
        
        if (!prefixIncluded) {
          // Check if user_id is available in Props
          if (_StateProps && typeof _StateProps.user_id === "string") {
            key = _StateProps.user_id + "_" + key;
          } else if (
            _StateProps?.redux &&
            typeof _StateProps.redux.user_id === "string"
          ) {
            key = _StateProps.redux.user_id + "_" + key;
          } else {
            // If no user_id available, store without prefix for now
            // This handles the case during isUserExistsInMulti call
            // console.log(`[globalState.set] No user_id available, storing without prefix: ${originalKey}`);
          }
        }
        
        SelectedStorage.setItem(key, value);
        
        // For critical keys like user_id, also store without prefix for backward compatibility
        if (originalKey === "user_id" || originalKey === "auth_token" || originalKey === "shop" || originalKey === "host") {
          SelectedStorage.setItem(originalKey, value);
        }
      },
  get:
    (_StateProps: any) =>
      (key: string, prefixIncluded = false): string | null => {
        if (!prefixIncluded) {
          // Try with user_id prefix first
          if (_StateProps && typeof _StateProps.user_id === "string") {
            const prefixedValue = SelectedStorage.getItem(_StateProps.user_id + "_" + key);
            if (prefixedValue) return prefixedValue;
          } else if (
            _StateProps?.redux &&
            typeof _StateProps.redux.user_id === "string"
          ) {
            const prefixedValue = SelectedStorage.getItem(_StateProps.redux.user_id + "_" + key);
            if (prefixedValue) return prefixedValue;
          }
          
          // Fallback to non-prefixed key (for backward compatibility)
          const nonPrefixedValue = SelectedStorage.getItem(key);
          if (nonPrefixedValue) return nonPrefixedValue;
        }
        
        return SelectedStorage.getItem(key);
      },
  removeLocalStorage:
    (_StateProps: any) =>
      (key: string, path = "/"): unknown => {
        let keysToRemove = [key];
        
        // Also remove prefixed versions if user_id is available
        if (_StateProps && typeof _StateProps.user_id === "string") {
          keysToRemove.push(_StateProps.user_id + "_" + key);
        } else if (
          _StateProps?.redux &&
          typeof _StateProps.redux.user_id === "string"
        ) {
          keysToRemove.push(_StateProps.redux.user_id + "_" + key);
        }
        
        // Remove all versions of the key
        keysToRemove.forEach(keyToRemove => {
          SelectedStorage.removeItem(keyToRemove);
        });
        
        return true;
      },
  getBearerToken: (_StateProps: any) => (prefixIncluded = true): string | null => {
    let token = null;
    
    // Try non-prefixed token first
    if (!prefixIncluded) {
      token = SelectedStorage.getItem("auth_token");
      if (token) {
        return token;
      }
    }
    
    // Try prefixed tokens
    if (_StateProps && typeof _StateProps.user_id === "string") {
      token = SelectedStorage.getItem(_StateProps.user_id + "_" + "auth_token");
      if (token) {
        return token;
      }
    } else if (
      _StateProps?.redux &&
      typeof _StateProps.redux.user_id === "string"
    ) {
      token = SelectedStorage.getItem(_StateProps.redux.user_id + "_" + "auth_token");
      if (token) {
        return token;
      }
    }
    
    // Fallback to environment Bearer
    if (!token) {
      token = environment.Bearer;
    }
    
    return token;
  },
  prepareQuery: (params: ObjectI): unknown => {
    let queryString = Object.keys(params).length > 0 ? "?" : "";
    const end = "";
    for (const element of Object.keys(params)) {
      let key = params[element];
      queryString += end + key + "=" + params[key];
      key = "&";
    }
    return queryString;
  },
};
